import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { NavMarker } from 'react-rhumb';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import Screens from '../../constants/routing/Screens';
import CompareTable from './CompareTable';
import StickyTabsContainer from '../common/StickyContainer';
import styled from 'styled-components';
import UIButton from 'UIComponents/button/UIButton';
import UIIcon from 'UIComponents/icon/UIIcon';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { PageHeader, PageTitle } from '../common/SharedComponents';
import { useCompare } from '../../hooks/useCompare';
import { useAppDispatch, useAppSelector } from '../../reducers';
import { fetchCompareStatus, getCompareApiNamesSelector } from '../../selectors/CompareSelectors';
import { UNINITIALIZED, PENDING, SUCCEEDED, FAILED } from '../../actions/constants/RequestStatus';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import { addAllToCompareProducts } from '../../actions/CompareActions';
import { stringifyQueryParams } from 'pricing-pages-lib/utils/queryParamUtils';
import CompareEmptyState from './CompareEmptyState';
import CompareErrorState from './CompareErrorState';
import { useValidatedQueryParamApiNamesForCompare } from '../../utils/urls/queryParamUtils';
import { ContainerStyles } from './CompareStyles';
const ViewportDiv = styled.div.withConfig({
  displayName: "ComparePage__ViewportDiv",
  componentId: "smjd6q-0"
})(["display:flex;flex-direction:column;min-height:90vh;"]);
const TabContainer = styled.div.withConfig({
  displayName: "ComparePage__TabContainer",
  componentId: "smjd6q-1"
})(["", " display:flex;align-items:center;"], ContainerStyles);
const MainContent = styled.main.attrs({
  role: 'main'
}).withConfig({
  displayName: "ComparePage__MainContent",
  componentId: "smjd6q-2"
})(["display:flex;flex-direction:column;flex-grow:1;margin-top:24px;margin-bottom:24px;"]);

const ComparePage = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const {
    pathname
  } = useLocation();
  const requestStatus = useAppSelector(fetchCompareStatus);
  const compareApiNames = useAppSelector(getCompareApiNamesSelector);
  const shouldUseQueryParams = useRef(true);
  const queryParamApiNames = useValidatedQueryParamApiNamesForCompare();
  useEffect(() => {
    if (stringifyQueryParams({
      products: compareApiNames
    }) === stringifyQueryParams({
      products: queryParamApiNames
    })) {
      return;
    }

    if (!compareApiNames.length && queryParamApiNames.length && shouldUseQueryParams.current) {
      dispatch(addAllToCompareProducts(queryParamApiNames));
    } else {
      const newQueryString = stringifyQueryParams({
        products: compareApiNames
      });
      const newUrl = `${pathname}?${newQueryString}`;
      history.replace(newUrl);
    }
  }, [dispatch, history, pathname, compareApiNames, queryParamApiNames]);
  useEffect(() => {
    getUsageTracker().setProperties({
      defaultedTab: '',
      tab: ''
    });
    window.scrollTo(0, 0);
    getUsageTracker().track('pageView', {
      screen: Screens.COMPARE
    });
    shouldUseQueryParams.current = false;
  }, []);
  useCompare({
    highlightedProducts: compareApiNames
  });
  return /*#__PURE__*/_jsxs(ViewportDiv, {
    children: [/*#__PURE__*/_jsx(NavMarker, {
      name: "COMPARE_PAGE"
    }), /*#__PURE__*/_jsx(StickyTabsContainer, {
      children: /*#__PURE__*/_jsx(TabContainer, {
        children: /*#__PURE__*/_jsxs(UIButton, {
          use: "link",
          onClick: () => {
            history.push('/marketing');
          },
          className: "m-y-auto m-right-auto",
          children: [/*#__PURE__*/_jsx(UIIcon, {
            name: "left",
            size: 28,
            color: "inherit",
            className: "m-right-0",
            style: {
              verticalAlign: 'middle'
            }
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: "compare.page.backToPricing"
          })]
        })
      })
    }), /*#__PURE__*/_jsxs(MainContent, {
      children: [/*#__PURE__*/_jsx(PageHeader, {
        children: /*#__PURE__*/_jsx(PageTitle, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "compare.page.title"
          })
        })
      }), /*#__PURE__*/_jsxs("section", {
        className: "flex-column justify-center align-center flex-grow-1 p-y-4",
        children: [(requestStatus === UNINITIALIZED || !compareApiNames.length) && /*#__PURE__*/_jsx(CompareEmptyState, {}), requestStatus === PENDING && /*#__PURE__*/_jsx(UILoadingSpinner, {
          grow: true
        }), requestStatus === SUCCEEDED && /*#__PURE__*/_jsx(CompareTable, {}), requestStatus === FAILED && /*#__PURE__*/_jsx(CompareErrorState, {})]
      })]
    })]
  });
};

export default ComparePage;